import React, { useEffect, useMemo, useState } from "react";
import {
  CButton,
  CModal,
  CModalTitle,
  CModalHeader,
  CModalBody,
  CModalFooter,
  CSpinner,
  CForm,
  CFormLabel,
  CFormInput,
  CFormSelect,
  CFormSwitch,
  CAlert,
  CTable,
} from "@coreui/react";
import { apiClient } from "services/Api";
import { triggerAlert } from "components/globals/AlertNotification";

const RESPONSIBLE_ENTITIES = [
  { value: "IAPMEI", label: "IAPMEI" },
  { value: "ANI", label: "ANI" },
  { value: "OUTRO", label: "Outro" },
];

const INVESTMENT_METHODS = [
  { value: "CUSTOS_REAIS", label: "Custos Reais" },
  { value: "CUSTOS_SIMPLIFICADOS", label: "Custos Simplificados" },
];

const PROJECT_STATE_LABELS = {
  APPROVED: "Aprovado",
  THEORETICAL: "Teórico",
};

const ANI_TRLS = [
  "Investigação Industrial",
  "Desenvolvimento Experimental",
  "Divulgação e Promoção",
  "Gestão de Projeto",
];

const IAPMEI_TRLS = ["TRL 3-4", "TRL 5-9"];

const EditProject = ({ clientId, projectId, onClose, setToast }) => {
  const [name, setName] = useState("");
  const [project, setProject] = useState(null);
  const [incentiveRates, setIncentiveRates] = useState({});
  const [errors, setErrors] = useState({});

  const loading = !project;

  const handleProjectEdit = (field, value) => {
    setProject((prev) => ({ ...prev, [field]: value }));
  };

  const handleProjectManagementEdit = (field, value) => {
    setProject((prev) => ({
      ...prev,
      management: { ...prev.management, [field]: value },
    }));
  };

  const handleRateChange = (trl, value) => {
    setIncentiveRates((prev) => ({
      ...prev,
      [trl]: value,
    }));
  };

  const currentTrls = useMemo(() => {
    const responsible = project?.management?.responsible_entity;
    if (responsible === "ANI") return ANI_TRLS;
    if (responsible === "IAPMEI") return IAPMEI_TRLS;
    return [];
  }, [project?.management?.responsible_entity]);

  const noIncentive = !!project?.management?.no_incentive;
  // O estado não se altera aqui: Teórico -> Aprovado é feito uma única vez
  // pela ação "Aprovar Projeto" (ApproveProject) e é irreversível.
  const isTheoretical = project?.state === "THEORETICAL";

  const renderFieldError = (field) => {
    const message = errors?.[field];
    if (!message) return null;
    const text = Array.isArray(message) ? message.join(" ") : message;
    return <small className="text-danger">{text}</small>;
  };

  const renderNonFieldErrors = () => {
    const message = errors?.non_field_errors;
    if (!message) return null;
    const text = Array.isArray(message) ? message.join(" ") : message;
    return (
      <CAlert color="danger" className="py-2">
        {text}
      </CAlert>
    );
  };

  const handleUpdateProject = () => {
    if (!project) return;

    setErrors({});

    const mgmt = project.management || {};
    const responsible_entity = mgmt.responsible_entity;
    const investment_methodology = mgmt.investment_methodology;

    const incentiveRatesPayload = noIncentive
      ? []
      : currentTrls
      .filter(
        (trl) =>
          incentiveRates[trl] !== undefined && incentiveRates[trl] !== "",
      )
      .map((trl) => ({
        trl,
        rate: Number(incentiveRates[trl]),
      }));

    const payload = {
      name: project.name,
      program: noIncentive ? "" : project.program,
      code: noIncentive ? "" : project.code,
      responsible_entity: noIncentive ? "OUTRO" : responsible_entity,
      investment_methodology: noIncentive
        ? "CUSTOS_REAIS"
        : investment_methodology,
      approved_budget: noIncentive ? 0 : mgmt.approved_budget,
      start_date: mgmt.start_date,
      end_date: mgmt.end_date,
      hour_cost:
        !noIncentive && investment_methodology === "CUSTOS_SIMPLIFICADOS"
          ? Number(mgmt.hour_cost || 0)
          : undefined,
      no_incentive: noIncentive,
      incentive_rates: incentiveRatesPayload,
    };

    apiClient
      .put(`/projects/management/${clientId}/${projectId}/project/`, payload)
      .then(() => {
        triggerAlert("Projeto atualizado com sucesso.", "success");
        window.location.reload();
      })
      .catch((error) => {
        console.error("Erro ao atualizar o projeto:", error);
        const resp = error.response;
        if (resp && resp.status === 400 && resp.data) {
          setErrors(resp.data);
          setToast({
            title: "Projeto",
            message:
              "Existem erros no formulário. Verifica os campos assinalados.",
            type: "warning",
          });
        } else {
          setToast({
            title: "Projeto",
            message: "Não foi possível atualizar o projeto.",
            type: "error",
          });
        }
      });
  };

  useEffect(() => {
    apiClient
      .get(`/projects/management/${clientId}/${projectId}/project/`)
      .then((response) => {
        const data = response.data;
        setName(data.name);
        setProject(data);

        // Preencher mapa de taxas de incentivo, se vier do backend
        const ratesMap = {};
        const mgmt = data.management || {};
        (data.incentive_rates || []).forEach((r) => {
          if (r.trl) ratesMap[r.trl] = r.rate;
        });
        setIncentiveRates(ratesMap);
      })
      .catch((error) => {
        console.error("Erro ao carregar dados do projeto:", error);
        setToast({
          title: "Erro",
          message: "Não foi possível carregar os dados do projeto.",
          type: "error",
        });
        onClose();
      });
  }, [clientId, projectId, onClose, setToast]);

  return (
    <CModal onClose={onClose} size="lg" backdrop="static" scrollable visible>
      <CModalHeader>
        <CModalTitle id="StaticBackdropExampleLabel">
          Editar Projeto {loading ? <CSpinner size="sm" /> : name}
        </CModalTitle>
      </CModalHeader>
      <CModalBody>
        {loading ? (
          <div className="d-flex justify-content-center py-4">
            <CSpinner />
          </div>
        ) : (
          <CForm className="row g-3">
            {renderNonFieldErrors()}

            <div className="mb-2 d-flex">
              <div className="col-8 pe-3">
                <CFormLabel htmlFor="projectName">
                  Nome do Projeto <span className="text-danger">*</span>
                </CFormLabel>
                <CFormInput
                  id="projectName"
                  type="text"
                  value={project.name || ""}
                  onChange={(e) => handleProjectEdit("name", e.target.value)}
                  placeholder="Introduz o nome do projeto"
                />
                {renderFieldError("name")}
              </div>
              <div className="col-4 d-flex align-items-center justify-content-end">
                <CFormSwitch
                  id="noIncentive"
                  label="Sem Incentivo"
                  checked={noIncentive}
                  disabled={isTheoretical && !noIncentive}
                  title={
                    isTheoretical && !noIncentive
                      ? "Aprova o projeto antes de o marcar como Sem Incentivo."
                      : undefined
                  }
                  onChange={(e) =>
                    handleProjectManagementEdit(
                      "no_incentive",
                      e.target.checked,
                    )
                  }
                />
              </div>
            </div>

            <div className="mb-3 d-flex">
              <div className="col-6 pe-3">
                <CFormLabel htmlFor="startDate">
                  Data de Início <span className="text-danger">*</span>
                </CFormLabel>
                <CFormInput
                  id="startDate"
                  type="date"
                  value={project.management?.start_date || ""}
                  onChange={(e) =>
                    handleProjectManagementEdit("start_date", e.target.value)
                  }
                />
                {renderFieldError("start_date")}
              </div>
              <div className="col-6 ps-2">
                <CFormLabel htmlFor="endDate">
                  Data de Conclusão <span className="text-danger">*</span>
                </CFormLabel>
                <CFormInput
                  id="endDate"
                  type="date"
                  value={project.management?.end_date || ""}
                  onChange={(e) =>
                    handleProjectManagementEdit("end_date", e.target.value)
                  }
                />
                {renderFieldError("end_date")}
              </div>
            </div>

            {!noIncentive && (
              <div className="mb-3 d-flex">
                <div className="col-6 pe-3">
                  <CFormLabel htmlFor="projectState">
                    Estado do Projeto
                  </CFormLabel>
                  <CFormInput
                    id="projectState"
                    type="text"
                    value={PROJECT_STATE_LABELS[project.state] || project.state || ""}
                    disabled
                    readOnly
                  />
                  {isTheoretical && (
                    <small className="text-muted">
                      Para aprovar o projeto usa o botão "Aprovar Projeto".
                    </small>
                  )}
                  {renderFieldError("state")}
                </div>
                <div className="col-6 ps-2">
                  <CFormLabel htmlFor="responsibleEntity">
                    Entidade Responsável <span className="text-danger">*</span>
                  </CFormLabel>
                  <CFormSelect
                    id="responsibleEntity"
                    value={project.management?.responsible_entity || ""}
                    options={RESPONSIBLE_ENTITIES}
                    onChange={(e) =>
                      handleProjectManagementEdit(
                        "responsible_entity",
                        e.target.value,
                      )
                    }
                  />
                  {renderFieldError("responsible_entity")}
                </div>
              </div>
            )}

            {!noIncentive && (
              <div className="mb-3 d-flex">
                <div className="col-6 pe-3">
                  <CFormLabel htmlFor="program">
                    Programa de Financiamento
                  </CFormLabel>
                  <CFormInput
                    id="program"
                    type="text"
                    value={project.program || ""}
                    onChange={(e) =>
                      handleProjectEdit("program", e.target.value)
                    }
                    placeholder="Introduz o programa de financiamento"
                  />
                </div>
                <div className="col-6 ps-2">
                  <CFormLabel htmlFor="code">Código do Projeto</CFormLabel>
                  <CFormInput
                    id="code"
                    type="text"
                    value={project.code || ""}
                    onChange={(e) => handleProjectEdit("code", e.target.value)}
                    placeholder="Introduz o código do projeto"
                  />
                </div>
              </div>
            )}

            {!noIncentive && (
              <div className="mb-3 d-flex">
                <div className="col-6 pe-2">
                  <CFormLabel htmlFor="investMethodology">
                    Tipologia do Investimento{" "}
                    <span className="text-danger">*</span>
                  </CFormLabel>
                  <CFormSelect
                    id="investMethodology"
                    value={project.management?.investment_methodology || ""}
                    options={INVESTMENT_METHODS}
                    onChange={(e) =>
                      handleProjectManagementEdit(
                        "investment_methodology",
                        e.target.value,
                      )
                    }
                  />
                  {renderFieldError("investment_methodology")}
                </div>

                {project.management?.investment_methodology ===
                  "CUSTOS_SIMPLIFICADOS" && (
                  <div className="col-6">
                    <CFormLabel>
                      Custo Unitário <span className="text-danger">*</span>
                    </CFormLabel>

                    <CFormInput
                      type="number"
                      value={project.management?.hour_cost ?? ""}
                      onChange={(e) =>
                        handleProjectManagementEdit("hour_cost", e.target.value)
                      }
                    />

                    {renderFieldError("hour_cost")}
                  </div>
                )}
              </div>
            )}
            {!noIncentive && (
              <div className="mb-3 d-flex">
                <div className="col-6 pe-3">
                  <CFormLabel htmlFor="approvedBudget">
                    Incentivo Aprovado
                  </CFormLabel>
                  <CFormInput
                    id="approvedBudget"
                    type="number"
                    value={project.management?.approved_budget ?? ""}
                    onChange={(e) =>
                      handleProjectManagementEdit(
                        "approved_budget",
                        e.target.value,
                      )
                    }
                    placeholder="Introduz o montante aprovado"
                  />
                </div>
              </div>
            )}

            {/* Tabela de taxas de incentivo por TRL */}
            {!noIncentive && currentTrls.length > 0 && (
              <div className="col-12 mb-3">
                <CFormLabel>Taxas de Incentivo por TRL</CFormLabel>
                <CTable small bordered className="mt-2">
                  <thead>
                    <tr>
                      <th>TRL</th>
                      <th>Taxa de Incentivo (%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentTrls.map((trl) => (
                      <tr key={trl}>
                        <td>{trl}</td>
                        <td>
                          <CFormInput
                            type="number"
                            min={0}
                            max={100}
                            value={incentiveRates[trl] ?? ""}
                            onChange={(e) =>
                              handleRateChange(trl, e.target.value)
                            }
                            placeholder="0"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </CTable>
                {renderFieldError("incentive_rates")}
              </div>
            )}
          </CForm>
        )}
      </CModalBody>
      <CModalFooter>
        <CButton color="secondary" onClick={onClose}>
          Cancelar
        </CButton>
        <CButton
          color="primary"
          onClick={handleUpdateProject}
          disabled={loading}
        >
          Atualizar Projeto
        </CButton>
      </CModalFooter>
    </CModal>
  );
};

export default EditProject;
